import React, { useState, useEffect } from "react";
import { Lock, Loader2, Copy, ArrowRightLeft } from "lucide-react";
import { ImSpinner3 } from "react-icons/im";
import { useSelector, useDispatch } from "react-redux";
import { getSubscription } from "../../services/paymentService";
import { getApiKeys, getUsageAnalytics, getApiLogs } from "../../services/developerService";
import { getUserDashboardData, getUserById } from "../../services/authService";
import { setUser } from "../../store/slices/authSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getStaticUrl } from "../../config/domains";
import { API_BASE_URL } from "../../constant/constant";
import TrialExpirationBanner from "./TrialExpirationBanner";
import TokenBalanceCard from "./TokenBalanceCard";
import PlanStatusCard from "./PlanStatusCard";
import TranslationFeedback from "./TranslationFeedback";

const MAX_TEXT_CHARACTERS = 600;

const Translation = () => {
    const dispatch = useDispatch();
    const { token, user, role } = useSelector((state) => state.auth);

    // Subscription & Data state
    const [checkingSubscription, setCheckingSubscription] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [hasSubscription, setHasSubscription] = useState(false);
    const [apiKey, setApiKey] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [logs, setLogs] = useState([]);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    // Translation state
    const [fromLanguage, setFromLanguage] = useState("English");
    const [toLanguage, setToLanguage] = useState("Yoruba");
    const [inputText, setInputText] = useState("");
    const [translatedText, setTranslatedText] = useState("");
    const [translating, setTranslating] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Translating...");
    const [remainingDays, setRemainingDays] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);

    useEffect(() => {
        const getUserDetails = async () => {
            const response = await getUserById(token);
            if (response) {
                setUserDetails(response);
            } else if (response?.message) {
                toast.error(response.message);
            }
        };
        getUserDetails();
    }, [token]);

    useEffect(() => {
        if (!userDetails?.tokenExpiration) return;

        const now = new Date();
        const exp = new Date(userDetails.tokenExpiration);

        const diffTime = exp.getTime() - now.getTime();
        const diffDays = Math.max(
            0,
            Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        );

        setRemainingDays(diffDays);
    }, [userDetails?.tokenExpiration]);

    // Progress through loading messages sequentially while translating
    useEffect(() => {
        if (!translating) {
            setLoadingMessage("Translating...");
            return;
        }

        const messages = [
            "Translating...",
            "Analyzing your text...",
            "Preparing your text..."
        ];
        let currentIndex = 0;
        setLoadingMessage(messages[0]); // Start with first message

        const interval = setInterval(() => {
            currentIndex++;
            if (currentIndex < messages.length) {
                setLoadingMessage(messages[currentIndex]);
            } else {
                clearInterval(interval); // Stop after showing all messages
            }
        }, 2000); // Change message every 2 seconds

        return () => clearInterval(interval);
    }, [translating]);

    useEffect(() => {
        const loadDashboardData = async () => {
            if (!token) {
                setCheckingSubscription(false);
                return;
            }

            try {
                setCheckingSubscription(true);

                // 1. Check subscription
                try {
                    const subResponse = await getSubscription(token);
                    if (subResponse.success && subResponse.hasSubscription && subResponse.subscription.status === 'active') {
                        setHasSubscription(true);
                        setSubscriptionDetails(subResponse.subscription);
                    } else {
                        setHasSubscription(false);
                    }
                } catch (error) {
                    console.error("Error fetching subscription:", error);
                    setHasSubscription(false);
                }

                // 2. Fetch API Keys
                try {
                    const keysResponse = await getApiKeys(token);
                    const activeKey = keysResponse.apiKeys?.find(key => key.isActive);
                    if (activeKey) {
                        setApiKey(activeKey.key);
                    }
                } catch (error) {
                    console.error("Error fetching API keys:", error);
                }

                // 3. Fetch Analytics
                try {
                    const analyticsResponse = await getUsageAnalytics('30d', token);
                    setAnalytics(analyticsResponse);
                } catch (error) {
                    console.error("Error fetching analytics:", error);
                }

                // 4. Fetch Logs
                try {
                    const logsResponse = await getApiLogs({ limit: 10 }, token);
                    if (logsResponse?.logs) {
                        const tttLogs = logsResponse.logs.filter(
                            log => (log.service && log.service.toUpperCase() === 'TTT') || (log.endpoint && log.endpoint.includes('translate'))
                        );
                        setLogs(tttLogs.length > 0 ? tttLogs : logsResponse.logs);
                    }
                } catch (error) {
                    console.error("Error fetching logs:", error);
                }

            } catch (error) {
                console.error("Error loading dashboard data:", error);
            } finally {
                setCheckingSubscription(false);
            }
        };

        loadDashboardData();
    }, [token]);

    const estimateTokenCost = (text) => {
        if (!text || !text.trim()) return 0;
        return Math.ceil(text.length / 4);
    };

    const checkTokenStatus = () => {
        if (!userDetails) {
            return { expired: false, exhausted: true };
        }
        const tokensAvailable = userDetails.tokens !== undefined && userDetails.tokens !== null && userDetails.tokens > 0;
        const exhausted = !tokensAvailable;
        let expired = false;
        if (userDetails.tokenExpiration) {
            const expirationDate = new Date(userDetails.tokenExpiration);
            const currentDate = new Date();
            expired = currentDate > expirationDate;
        }
        return { expired, exhausted };
    };

    const getAvailableTargetLanguages = (sourceLang) => {
        if (sourceLang === "English") {
            return ["Yoruba", "Swahili", "Nigerian Pidgin"];
        } else if (sourceLang === "Yoruba" || sourceLang === "Swahili" || sourceLang === "Nigerian Pidgin") {
            return ["English"];
        }
        return ["English"];
    };

    const getAvailableSourceLanguages = (targetLang) => {
        if (targetLang === "English") {
            return ["Yoruba", "Swahili", "Nigerian Pidgin"];
        } else if (targetLang === "Yoruba" || targetLang === "Swahili" || targetLang === "Nigerian Pidgin") {
            return ["English"];
        }
        return ["English"];
    };

    const handleFromLanguageChange = (lang) => {
        setFromLanguage(lang);
        const availableLanguages = getAvailableTargetLanguages(lang);
        setToLanguage(availableLanguages[0]);
    };

    const handleToLanguageChange = (lang) => {
        setToLanguage(lang);
        const availableLanguages = getAvailableSourceLanguages(lang);
        setFromLanguage(availableLanguages[0]);
    };

    const handleTextChange = (e) => {
        const nextValue = e.target.value;
        if (nextValue.length > MAX_TEXT_CHARACTERS) {
            setInputText(nextValue.slice(0, MAX_TEXT_CHARACTERS));
            return;
        }
        setInputText(nextValue);
    };

    const handleTextPaste = (e) => {
        const pasted = e.clipboardData?.getData("text") ?? "";
        const selectionStart = e.target.selectionStart ?? inputText.length;
        const selectionEnd = e.target.selectionEnd ?? inputText.length;
        const nextLength = inputText.length - (selectionEnd - selectionStart) + pasted.length;
        if (nextLength > MAX_TEXT_CHARACTERS) {
            toast.error(`Text is limited to ${MAX_TEXT_CHARACTERS} characters.`);
        }
    };

    const handleSwapLanguages = () => {
        const tempFrom = fromLanguage;
        const tempTo = toLanguage;
        setFromLanguage(tempTo);
        setToLanguage(tempFrom);
        if (translatedText.length > MAX_TEXT_CHARACTERS) {
            setInputText(translatedText.slice(0, MAX_TEXT_CHARACTERS));
            toast.error(`Text is limited to ${MAX_TEXT_CHARACTERS} characters.`);
        } else {
            setInputText(translatedText);
        }
        setTranslatedText(inputText);
    };

    const handleConvert = async () => {
        if (!inputText.trim()) return;
        if (inputText.length > MAX_TEXT_CHARACTERS) {
            toast.error(`Text must be ${MAX_TEXT_CHARACTERS} characters or fewer.`);
            return;
        }
        if (fromLanguage === toLanguage) {
            toast.error("Please select different languages.");
            return;
        }

        if (!apiKey) {
            toast.error("No active API key found. Please generate one in Developer Tools.");
            return;
        }

        const { expired, exhausted } = checkTokenStatus();
        if (expired) {
            toast.error("Your tokens have expired. Please purchase a plan or contact support.");
            return;
        }

        if (exhausted) {
            toast.error("You have used all your available tokens. Please purchase a plan or contact support.");
            return;
        }

        setTranslating(true);
        const API_URL = `${API_BASE_URL}/translate/with-api-key`;
        const payload = {
            text: inputText,
            fromLanguage: fromLanguage,
            toLanguage: toLanguage
        };
        const headers = {
            "Authorization": apiKey,
            "Content-Type": "application/json"
        };

        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: headers,
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (!response.ok) {
                const errorMessage = result.message || result.error || result.errorMessage || `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            setTranslatedText(result.translated_text);
            setShowFeedback(true);

            const logsResponse = await getApiLogs({ limit: 10 }, token);
            if (logsResponse?.logs) {
                const tttLogs = logsResponse.logs.filter(
                    log => (log.service && log.service.toUpperCase() === 'TTT') || (log.endpoint && log.endpoint.includes('translate'))
                );
                setLogs(tttLogs.length > 0 ? tttLogs : logsResponse.logs);
            }

            try {
                const dashboardData = await getUserDashboardData(token);
                if (dashboardData?.overview?.tokenBalance?.available !== undefined) {
                    const newTokenBalance = dashboardData.overview.tokenBalance.available;
                    if (userDetails) {
                        setUserDetails(prev => ({
                            ...prev,
                            tokens: newTokenBalance
                        }));
                    }
                    if (user) {
                        dispatch(setUser({
                            user: { ...user, tokens: newTokenBalance },
                            role: role || 'user'
                        }));
                    }
                }
            } catch (error) {
                console.error("Error refreshing token balance:", error);
            }

            const analyticsResponse = await getUsageAnalytics('30d', token);
            setAnalytics(analyticsResponse);

        } catch (error) {
            console.error("Error:", error);
            const errorMessage = error.message || "Translation failed. Please try again.";
            toast.error(errorMessage);
        } finally {
            setTranslating(false);
        }
    };

    const copyToClipboard = (text) => {
        if (text) {
            navigator.clipboard.writeText(text);
            toast.success("Text copied!");
        }
    };

    if (checkingSubscription) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const { expired: tokensExpired, exhausted: tokensExhausted } = checkTokenStatus();
    const shouldLockFeature = tokensExpired || tokensExhausted;

    if (shouldLockFeature) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[600px]">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-xl w-full text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="w-10 h-10 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        Premium feature locked
                    </h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        This feature is available for our active subscribers. You’re welcome to try our free, slower models in the playground.
                    </p>
                    <button
                        onClick={() => window.open(getStaticUrl('/playground'), '_blank', 'noopener,noreferrer')}
                        className="w-full bg-secondary hover:bg-teal-800 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200"
                    >
                        Use free playground
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            <ToastContainer position="top-right" autoClose={3000} />
            {userDetails?.purchaseHistory?.length === 0 && <TrialExpirationBanner user={userDetails} />}

            {/* Top Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TokenBalanceCard
                    userDetails={userDetails}
                    remainingDays={remainingDays}
                    subscriptionDetails={subscriptionDetails}
                    analytics={analytics}
                />
                <PlanStatusCard
                    subscriptionDetails={subscriptionDetails}
                />
            </div>

            {/* Translation Interface */}
            <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Input Section */}
                    <div className="space-y-4">
                        <div className="rounded-xl border border-gray-200 shadow-sm px-4 py-3" style={{ backgroundColor: '#FDFDFD' }}>
                            <select
                                value={fromLanguage}
                                onChange={(e) => handleFromLanguageChange(e.target.value)}
                                className="bg-transparent font-semibold text-gray-900 outline-none cursor-pointer w-full"
                            >
                                <option value="English">English</option>
                                <option value="Yoruba">Yoruba</option>
                                <option value="Swahili">Swahili</option>
                                <option value="Nigerian Pidgin">Nigerian Pidgin</option>
                            </select>
                        </div>

                        <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-[300px]" style={{ backgroundColor: '#FDFDFD' }}>
                            <textarea
                                value={inputText}
                                onChange={handleTextChange}
                                onPaste={handleTextPaste}
                                maxLength={MAX_TEXT_CHARACTERS}
                                placeholder="Enter text to translate..."
                                className="flex-1 min-h-0 w-full resize-none outline-none text-lg text-gray-800 placeholder-gray-400 p-4 bg-transparent"
                            />
                            <div className="shrink-0 border-t border-gray-100 px-4 py-3 flex flex-col gap-2">
                                <span className={`text-sm ${inputText.length >= MAX_TEXT_CHARACTERS ? "text-red-500 font-medium" : "text-gray-400"}`}>
                                    {inputText.length}/{MAX_TEXT_CHARACTERS} characters
                                    {inputText.length >= MAX_TEXT_CHARACTERS ? " • Limit reached" : ""}
                                </span>
                                <div className="flex items-center justify-end gap-3">
                                    {inputText.trim() && (
                                        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                                            Estimated: {estimateTokenCost(inputText)} tokens
                                        </span>
                                    )}
                                    <button
                                        onClick={handleConvert}
                                        disabled={translating || !inputText.trim() || fromLanguage === toLanguage || inputText.length > MAX_TEXT_CHARACTERS}
                                        className={`px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${translating || fromLanguage === toLanguage || inputText.length > MAX_TEXT_CHARACTERS
                                            ? "bg-gray-400 cursor-not-allowed text-white"
                                            : "bg-[#1e6c56] hover:bg-[#175443] text-white"
                                            }`}
                                    >
                                        Translate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Swap Button (desktop) */}
                    <div className="hidden md:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2 z-10">
                        <button
                            onClick={handleSwapLanguages}
                            className="p-3 rounded-full transition-shadow hover:bg-gray-50"
                            title="Swap languages"
                        >
                            <ArrowRightLeft className="w-6 h-6 text-gray-700" />
                        </button>
                    </div>

                    {/* Swap Button (mobile) */}
                    <div className="md:hidden flex items-center justify-center -my-3">
                        <button
                            onClick={handleSwapLanguages}
                            className="p-3 rounded-full transition-shadow hover:bg-gray-50 z-10"
                            title="Swap languages"
                        >
                            <ArrowRightLeft className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>

                    {/* Output Section */}
                    <div className="space-y-4">
                        <div className="rounded-xl border border-gray-200 shadow-sm px-4 py-3" style={{ backgroundColor: '#FDFDFD' }}>
                            <select
                                value={toLanguage}
                                onChange={(e) => handleToLanguageChange(e.target.value)}
                                className="bg-transparent font-semibold text-gray-900 outline-none cursor-pointer w-full"
                            >
                                {getAvailableTargetLanguages(fromLanguage).map((lang) => (
                                    <option key={lang} value={lang}>
                                        {lang}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ backgroundColor: '#FDFDFD' }}>
                            <div className="p-4 relative h-[300px]">
                                {translating ? (
                                    <div className="text-gray-500 text-lg h-full flex flex-col items-center justify-center gap-3">
                                        <ImSpinner3 className="animate-spin text-3xl text-[#1e6c56]" />
                                        <span className="font-medium text-base">{loadingMessage}</span>
                                    </div>
                                ) : translatedText ? (
                                    <div className="text-lg text-gray-800 font-medium h-full pb-16 overflow-y-auto">
                                        {translatedText}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 text-lg h-full flex items-center justify-center">
                                        Translation will appear here
                                    </div>
                                )}

                                <div className="absolute bottom-4 right-4 flex gap-2">
                                    <button
                                        onClick={() => copyToClipboard(translatedText)}
                                        disabled={!translatedText}
                                        className="px-4 py-2 bg-[#1e6c56] text-white rounded-lg font-medium hover:bg-[#175443] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <Copy className="w-4 h-4" />
                                        Copy text
                                    </button>
                                    <button
                                        onClick={() => {
                                            setTranslatedText("");
                                            setInputText("");
                                            setShowFeedback(false);
                                        }}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Translation Feedback Card */}
            {translatedText && !translating && showFeedback && (
                <TranslationFeedback
                    input={inputText}
                    output={translatedText}
                    fromLanguage={fromLanguage}
                    toLanguage={toLanguage}
                    apiEndpoint="/translate/with-api-key"
                    onHide={() => setShowFeedback(false)}
                />
            )}

            {/* Translation History */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Translation History</h3>
                <div className="space-y-6">
                    {logs.length > 0 ? (
                        logs.map((log, index) => {
                            const actionParts = (log.action || '').split(' • ');
                            const inputText = log.input || actionParts[1] || actionParts[0] || "Translation Request";

                            let sourceLang = log.sourceLang || "Auto";
                            let targetLang = log.targetLang || "Target";
                            if (actionParts[0] && actionParts[0].includes(' to ')) {
                                const parts = actionParts[0].split(/\s+to\s+/i);
                                if (parts.length === 2) {
                                    sourceLang = parts[0].trim();
                                    targetLang = parts[1].trim();
                                }
                            }

                            const tokensUsed = log.token ?? log.tokensUsed ?? 0;
                            const timeDisplay = log.when || (log.createdAt ? new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now');

                            return (
                                <div key={log.id || log._id || index} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className="font-medium text-gray-900 line-clamp-1 max-w-[70%]">
                                            {inputText}
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            {timeDisplay}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <span>
                                            {sourceLang} to {targetLang}
                                        </span>
                                        <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs font-medium">
                                            {tokensUsed} Tokens used
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center text-gray-500 py-8">
                            No translation history yet
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Translation;
